import * as uuid from "uuid";

import { DesignAction, IControlBaseAttributes } from "../../base/models";
import { Draggable, Droppable } from "../dnd";
import { HoverMenu, HoverMenuItem } from "../widgets";
import React, { memo, useEffect, useRef, useState } from "react";
import { createControl, readClipboardControlAsync, resetControlIds } from "../helpers";
import { getContainerLayoutClasses, getElementLayoutClasses, getLayoutType } from "../../base/styles/helpers/layout";

import { ComponentsDialog } from "../../base/register";
import { ControlCollection } from "./collections";
import { ControlType } from "../../base/types";
import { DndItem } from "../dnd/models";
import { PageControl } from "./PageControl";
import clsx from "clsx";
import { getOtherClasses } from "../../base/styles/helpers";
import { useDataContext } from "../page/context";
import { useDesignContext } from "../../providers";
import { useKeyboardHandler } from "../hooks";
import { useToaster } from "../../layout/toast";

export const PageControlDesigner = memo((props: IControlBaseAttributes) => {
    const { control, orientation, children, onDesignChange } = props;
    const ref = useRef<HTMLInputElement>(null)
    const [dialog, showDialog] = useState<boolean>(false);

    const { showToaster } = useToaster()
    const { keyCode, reset } = useKeyboardHandler<HTMLInputElement>(ref)
    const { control: { setContext, clearContext } } = useDesignContext()
    const { dataSource } = useDataContext()

    useEffect(() => {
        if (!keyCode) return;
        reset()

        const { controlId } = control
        switch (keyCode) {
            case "Delete":
                clearContext()
                onDesignChange({ type: DesignAction.DeleteControl, controlId });
                break;
            case "ShiftUp":
                onDesignChange({ type: DesignAction.ShiftControlUp, controlId });
                break;
            case "ShiftDown":
                onDesignChange({ type: DesignAction.ShiftControlDown, controlId });
                break;
            case "Copy":
                copyControlAsync()
                break;
            case "Cut":
                cutControlAsync()
                clearContext()
                break;
            case "Paste":
                pasteControlAsync()
                break;
        }
        // eslint-disable-next-line
    }, [keyCode])

    const cutControlAsync = async () => {
        await copyControlAsync()
        onDesignChange({ type: DesignAction.DeleteControl, controlId: control.controlId })
    }

    const copyControlAsync = async () => {
        const json = JSON.stringify({ ...control, controlId: uuid.v1() });
        await navigator.clipboard.writeText("::PAGE-CONTROL:: " + json);
        showToaster('Control copied to clipboard.')
    }

    const pasteControlAsync = async () => {
        const controlToPaste = await readClipboardControlAsync()
        if (!controlToPaste) {
            showToaster("Not a valid page control")
            return
        }

        onDesignChange({
            type: DesignAction.InsertAfterControl,
            controlId: control.controlId,
            control: resetControlIds(controlToPaste)
        })
    }

    const onFocusControlSelection = (e: React.FocusEvent) => {
        e.stopPropagation()
        e.preventDefault()

        if (!ref.current)
            return

        const elements = window.document.getElementsByClassName("pfx-control__selector")
        for (let i = 0; i < elements.length; i++) {
            const element = elements.item(i) as HTMLInputElement | null;
            if (element) {
                element.checked = false
            }
        }

        if (!ref.current.checked) {
            ref.current.checked = true
        }
    }

    const onClickPageControlElement = (e: React.MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()

        if (ref.current) {
            ref.current.focus()
            ref.current.checked = true
            setContext({ element: control, dataSource })
        }
    }

    const onSelectComponent = (type: ControlType) => {
        setTimeout(() => {
            const newControl = createControl(type)
            onDesignChange({
                type: DesignAction.InsertFirstControl,
                controlId: control.controlId,
                control: newControl
            });

        }, 50)
    }

    const onDropItem = (item: DndItem) => {
        if (item.type !== "Control") return

        onDesignChange({
            type: DesignAction.DropOnFirstControl,
            controlId: control.controlId,
            control: item.control
        });
    }

    const onHoverMenuItemClick = (item: HoverMenuItem) => {
        const { controlId } = control

        switch (item) {
            case "Cut":
                cutControlAsync()
                break;
            case "Copy":
                copyControlAsync()
                break;
            case "Paste":
                pasteControlAsync()
                break;
            case "Delete":
                onDesignChange({ type: DesignAction.DeleteControl, controlId });
                break;
        }
    }

    const getDisplayStyle = (type: ControlType): React.CSSProperties | undefined => {
        switch (type) {
            case ControlType.Repeater: return { display: "table", width: "100%" };
            case ControlType.RepeaterColumn: return { display: "table-cell" };

            case ControlType.Hyperlink: return { display: "inline-block" }
            case ControlType.Media: return { display: "inline-block" }
            case ControlType.Image: return { display: "inline-block" }
            case ControlType.Video: return { display: "inline-block" }
            case ControlType.MaterialIcon: return { display: "inline-block" }
            case ControlType.Badge: return { display: "inline-block" }
            case ControlType.Button: return { display: "inline-block" }
            case ControlType.CardNavigation: return { display: "inline-block" }
            case ControlType.ListNavigation: return { display: "inline-block" }
            case ControlType.Offcanvas: return { display: "inline-block" }
            case ControlType.SubmitButton: return { display: "inline-block" }
            case ControlType.Label: return { display: "inline-block" }
            case ControlType.NameLookup: return { display: "inline-block" }
            case ControlType.PayUButton: return { display: "inline-block" }
            case ControlType.PaytmButton: return { display: "inline-block" }
            case ControlType.RazorPayButton: return { display: "inline-block" }
            case ControlType.HiddenInput: return { display: "inline-block" }

            default: return { display: "block" };
        }
    }

    const dataAttrs = {
        "data-control-id": control.controlId,
        "data-component-type": control.componentType
    }

    const displayStyle = getDisplayStyle(control.componentType)
    const childLayout = getLayoutType(control)
    const controlOrientation = orientation === "Horizontal" ? "pfx-horizontal" : "pfx-vertical"
    const otherClasses = getOtherClasses(control.styles);
    const containerLayoutClasses = getContainerLayoutClasses(control.styles);
    const elementLayoutClasses = getElementLayoutClasses(control.styles);
    const controlClass = clsx("pfx-control", elementLayoutClasses, controlOrientation)

    return (
        <>
            <div  {...dataAttrs}
                style={displayStyle}
                title={`${control.componentType}`}
                className={controlClass}
                onClick={onClickPageControlElement}>

                <HoverMenu onClick={onHoverMenuItemClick} />

                <Draggable className="pfx-control__body" item={{ type: "Control", control }}>
                    <input type="radio"
                        ref={ref}
                        id={control.controlId}
                        name="selector"
                        className="pfx-control__selector"
                        onFocusCapture={onFocusControlSelection} />

                    <PageControl {...props} className={clsx(containerLayoutClasses, otherClasses, control.className, "flex-wrap")}>
                        <Droppable
                            onClick={(e) => {
                                e.stopPropagation();
                                showDialog(true)
                            }}
                            onDropItem={onDropItem}
                            layout={"Auto"}
                            title={`Click to insert a new component`} />

                        <ControlCollection
                            control={control}
                            controls={control.controls || []}
                            orientation={childLayout}
                            onDesignChange={onDesignChange} />
                    </PageControl>
                </Draggable>

                <>
                    {/* Droppable from child controls */}
                    {children}
                </>
            </div>

            <ComponentsDialog
                show={dialog}
                onSelect={onSelectComponent}
                onHideModal={() => {
                    clearContext();
                    showDialog(false);
                }}
            />
        </>
    )
})

